import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { Building2, Search, Plus, RefreshCcw, CalendarDays } from "lucide-react";

const BASE_URL = "https://nissi-india.com"; // change if needed

const DutyCardListingNew = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  // ✅ consistent button colors
  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const finYearId = localStorage.getItem("finYearId") || "";
  const branchIdLS = localStorage.getItem("branchId") || "";
  const branchTypeLS = localStorage.getItem("branchType") || "HO";

  // filters
  // ✅ use YYYY-MM-DD for native date picker
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [empName, setEmpName] = useState("");
  const [empCode, setEmpCode] = useState("");
  const [designation, setDesignation] = useState("Select");
  const [branch, setBranch] = useState("Select");

  // dropdown data
  const [branches, setBranches] = useState([]);
  const [designations, setDesignations] = useState([]);

  // list
  const [rows, setRows] = useState([]);
  const [grandTotal, setGrandTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const screenBadge = useMemo(() => "INFRA SCREEN : C-17", []);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  // Branch ddl mapping helper (SP output differs)
  const getBranchValue = (b) =>
    b?.fld_branchid ?? b?.branchid ?? b?.id ?? b?.Value ?? b?.value ?? b?.fld_BranchID ?? "";
  const getBranchText = (b) =>
    b?.fld_branchname ?? b?.branchname ?? b?.name ?? b?.Text ?? b?.text ?? b?.fld_BranchName ?? "";

  const requireBasics = () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return false;
    }
    if (!String(branchIdLS).trim() || !String(branchTypeLS).trim()) {
      toast.warn("Branch details missing. Please login again.");
      return false;
    }
    return true;
  };

  // ✅ helpers for edit navigation
  const toYMD = (val) => {
    if (!val) return "";
    if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}$/.test(val)) return val;

    const dt = val instanceof Date ? val : new Date(val);
    if (Number.isNaN(dt.getTime())) return "";

    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  // ✅ get row branchId/date (MUST be from list API row)
  const getRowBranchId = (r) =>
    r?.fld_branchid ??
    r?.branchid ??
    r?.fld_BranchID ??
    r?.fld_branchId ??
    r?.BranchID ??
    "";

  // ⚠️ SET THIS CORRECTLY BASED ON YOUR /api/duty-card/list response
  const getRowDate = (r) =>
    r?.fld_date ??          // common
    r?.fld_DutyDate ??      // possible
    r?.fld_attdate ??       // possible
    r?.DutyDate ??          // possible
    r?.date ??              // possible
    "";

  const fetchFilters = async () => {
    if (!requireBasics()) return;

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/duty-card/filters`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(branchIdLS),
          branchType: String(branchTypeLS),
        },
      });

      if (res?.data?.success) {
        const data = res.data.rows || {};
        setBranches(data.branches || []);
        setDesignations(data.designations || []);

        // ASP: ddl_branch.SelectedValue = Session["branchid"]
        if (data.branchid) setBranch(String(data.branchid));
      } else {
        toast.error(res?.data?.message || "Failed to load filters.");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Something went wrong.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const fetchList = async (p = page) => {
    if (!requireBasics()) return;

    // ✅ EXACT ASP rule: only one date filled => "Select Date"
    const hasFrom = !!String(fromDate).trim();
    const hasTo = !!String(toDate).trim();
    if ((hasFrom && !hasTo) || (!hasFrom && hasTo)) {
      toast.warn("Select Date");
      return;
    }

    // ✅ HO special: ASP overwrites session branchid with selected ddl value
    const effectiveBranchId =
      String(branchTypeLS).toUpperCase() === "HO" && branch !== "Select" && branch
        ? Number(branch)
        : Number(branchIdLS);

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/duty-card/list`, {
        params: {
          finYearId: Number(finYearId),
          branchId: effectiveBranchId,
          branchType: String(branchTypeLS),

          page: p,
          pageSize,

          // native date input gives YYYY-MM-DD, backend accepts it
          fromDate: hasFrom ? fromDate : "",
          toDate: hasTo ? toDate : "",

          empcode: empCode.trim(),
          empname: empName.trim(),
          designation: designation === "Select" ? "" : designation,
          branch: branch === "Select" ? "" : branch,
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
        setPage(res.data.page || p);
        setGrandTotal(Number(res.data.grandTotal || 0));
      } else {
        toast.warn(res?.data?.message || "No Records Found");
        setRows([]);
        setTotal(0);
        setTotalPages(1);
        setGrandTotal(0);
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Something went wrong.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      await fetchFilters();
      await fetchList(1);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    setPage(1);
    await fetchList(1);
  };

  const handleViewAll = async () => {
    // ASP ClearContents()
    setEmpCode("");
    setEmpName("");
    setFromDate("");
    setToDate("");
    setDesignation("Select");
    // Branch is NOT cleared in ASP
    setPage(1);
    await fetchList(1);
  };

  const handleAddNew = () => {
    // ASP equivalent: Session.Remove("DailyID")
    sessionStorage.removeItem("DailyID"); // safe even if not set

    // Navigate to Entry page
    navigate("/PayRoll/DutyCardEntry");
  };

  const handleEdit = (dailyId) => {
    // ASP: Session["DailyID"] = id; Response.Redirect(...)
    sessionStorage.setItem("DailyID", String(dailyId));
    navigate("/PayRoll/DutyCardEntry");
  };

  const openViewPopup = (employeeId) => {
    const url = `/payroll/duty-card-view-new?Type=3&EmpID=${encodeURIComponent(employeeId)}`;
    window.open(
      url,
      "_blank",
      "width=900,height=900,top=50,left=50,resizable=no,scrollbars=yes,toolbar=no,status=no"
    );
  };

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">DUTY CARD LISTING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${
                  isDark ? "bg-white/10" : "bg-gray-100"
                }`}
              >
                <Building2 className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Bata / Duty Card Listing
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Search / View All / Pagination / Grand Total
                </div>
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {screenBadge}
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"
                }`}
              >
                {total} Records
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-amber-900/40 text-amber-100" : "bg-amber-100 text-amber-800"
                }`}
              >
                Grand Total: {Number(grandTotal || 0).toFixed(2)}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={() => fetchList(page)} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>

              <button onClick={handleAddNew} className={primaryBtn}>
                <Plus className="w-4 h-4" />
                Add New
              </button>
            </div>
          </div>

          {/* Filters */}
          <form onSubmit={handleSearch} className={cardBase + " p-6 mb-6"}>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
              <div>
                <label className={labelBase}>FromDate</label>
                <div className="mt-2 flex items-center gap-2">
                  <CalendarDays className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className={inputBase}
                  />
                </div>
              </div>

              <div>
                <label className={labelBase}>ToDate</label>
                <div className="mt-2 flex items-center gap-2">
                  <CalendarDays className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className={inputBase}
                  />
                </div>
              </div>

              <div>
                <label className={labelBase}>Employee Name</label>
                <input
                  value={empName}
                  onChange={(e) => setEmpName(e.target.value)}
                  placeholder="Type employee name..."
                  className={inputBase}
                />
              </div>

              <div>
                <label className={labelBase}>Employee Code</label>
                <input
                  value={empCode}
                  onChange={(e) => setEmpCode(e.target.value)}
                  placeholder="Type employee code..."
                  className={inputBase}
                />
              </div>

              <div>
                <label className={labelBase}>Designation</label>
                <select value={designation} onChange={(e) => setDesignation(e.target.value)} className={inputBase}>
                  <option value="Select">Select</option>
                  {designations.map((d) => (
                    <option key={d.id} value={d.name}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelBase}>Branch</label>
                <select value={branch} onChange={(e) => setBranch(e.target.value)} className={inputBase}>
                  <option value="Select">Select</option>
                  {branches.map((b, i) => {
                    const value = getBranchValue(b);
                    const text = getBranchText(b);
                    return (
                      <option key={value || i} value={String(value)}>
                        {text || String(value)}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div className="flex items-end gap-3">
                <button type="submit" className={primaryBtn} disabled={loading}>
                  <Search className="w-4 h-4" />
                  Search
                </button>

                <button type="button" onClick={handleViewAll} className={ghostBtn} disabled={loading}>
                  View All
                </button>
              </div>
            </div>
          </form>

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold">S.No</th>
                    <th className="px-4 py-4 text-center font-bold">Branch</th>
                    <th className="px-4 py-4 text-left font-bold">Employee Name</th>
                    <th className="px-4 py-4 text-center font-bold">Employee Code</th>
                    <th className="px-4 py-4 text-center font-bold">No of Days</th>
                    <th className="px-4 py-4 text-center font-bold">Rate</th>
                    <th className="px-4 py-4 text-center font-bold">Total Amount</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 7 }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => {
                      const employeeId = r.fld_employeeid; // needed for View popup

                      // ✅ Pick correct branch/date from row for navigation
                      const rowBranchId = String(getRowBranchId(r) || "");
                      const rowDateYMD = toYMD(getRowDate(r));

                      return (
                        <tr
                          key={`${employeeId || "row"}-${idx}`}
                          className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${isDark ? "bg-gray-800" : "bg-gray-50"}`}
                        >
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {(page - 1) * pageSize + idx + 1}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_BranchName}
                          </td>

                      <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-200" : "text-gray-900"}`}>
  <button
    type="button"
    className="font-bold underline underline-offset-2 hover:opacity-80"
    onClick={() => {
      // ✅ ONLY pass employeeId
      const employeeId =
        r.fld_Employeeid ?? r.fld_employeeid ?? r.employeeId ?? r.empId;

      if (!employeeId) {
        toast.error("Employee ID not found for this row");
        return;
      }

      navigate(`/payroll/dutycard/view/${encodeURIComponent(String(employeeId))}`);
    }}
    title="Open Duty Card View & Update"
  >
    {r.fld_employee}
  </button>
</td>





                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_employeecode}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_noofdays}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_dailybata}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {Number(r.fld_totalamount || 0).toFixed(2)}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>

                {!loading && rows.length > 0 && (
                  <tfoot>
                    <tr className={isDark ? "bg-gray-900" : "bg-white"}>
                      <td colSpan={6} className={`px-4 py-4 text-right font-extrabold ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                        Grand Total :
                      </td>
                      <td className={`px-4 py-4 text-center font-extrabold ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                        {Number(grandTotal || 0).toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
            </div>

            {totalPages > 1 && (
              <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === 1
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Prev
                </button>

                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i + 1)}
                    className={`px-4 py-2 text-sm font-bold transition ${
                      page === i + 1
                        ? `${primaryBtnClass} text-white`
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === totalPages
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default DutyCardListingNew;
